import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useIsFocused, useRouter } from 'expo-router';
import {
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useColorScheme,
  useWindowDimensions,
} from 'react-native';
import { Paragraph, XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled AlertIcon/MessageIcon single-glyph
// components (identical lucide marks).
import {
  AlertCircle as AlertIcon,
  ChevronDown,
  MessageSquare as MessageIcon,
  SendHorizontal as SendIcon,
} from 'lucide-react-native';

import { Text } from '@/components/text';
import { AttachmentList } from '@/components/attachment-list';
import { AttachmentPicker } from '@/components/attachment-picker';
import { Linkified } from '@/components/linkified';
import { EmptyState } from '@/components/empty-state';
import { Heading } from '@/components/heading';
import { OverflowMenu } from '@/components/overflow-menu';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ResourceListFooter, StaleRefreshNotice } from '@/components/resource-list-view';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { Skeleton } from '@/components/skeleton';
import { TextArea } from '@/components/text-area';
import { useAuth } from '@/hooks/auth-context';
import { useAllConnections } from '@/hooks/use-connections';
import { useAttachmentPicker } from '@/hooks/use-attachment-picker';
import { usePaginatedResource } from '@/hooks/use-paginated-resource';
import { api, ApiError, type ConnectionsPage, type Message } from '@/lib/api';
import { hasSendableContent } from '@/lib/attachments';
import { displayHandle } from '@/lib/avatar-identity';
import { setConnectionMuted } from '@/lib/connections-cache';
import { MAX_MESSAGE_LEN } from '@/lib/content-limits';
import { composerPillLabel, conversationStatus, flattenMessagePages, hasOlderMessages } from '@/lib/conversation';
import { queryKeys } from '@/lib/query-keys';
import { relativeTime } from '@/lib/relative-time';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';
import { hit } from '@/lib/hit-slop';

// The 1:1 conversation between two people who have mutually revealed
// (issue #64) -- the destination the reveal moment was always missing.
// Reachable from a match in the Matches tab; stage 2 adds a Messages
// tab as a second door to this same screen.
//
// Polling, not realtime: design.md's settled v1 stance ("Realtime
// skipped for v1"). Realtime is tracked as issue #320.
const MESSAGE_POLL_MS = 5000;

// Whether this conversation is gone (a 404), read off the query's own
// live state rather than the screen's own `status` -- `status` is derived
// from this same query, so it cannot also be an input to it. Goes through
// conversationStatus so the poll and the render can never disagree about
// what a 404 means. Structurally typed rather than importing query-core's
// QueryState, since only these three fields are ever read.
function isGoneState(state: { status: string; data: unknown; error: unknown }): boolean {
  return (
    conversationStatus({
      isPending: state.status === 'pending',
      isError: state.status === 'error',
      hasData: state.data !== undefined,
      errorStatus: state.error instanceof ApiError ? state.error.status : undefined,
    }) === 'gone'
  );
}

export function Messages({ connectionId }: { connectionId: string | undefined }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;
  // Polling is gated on this screen actually being the one on screen --
  // a chat left mounted behind the profile it links to (or behind the
  // drawer) has nothing visible to keep fresh, and every tick is a real
  // request. TanStack's own refetchIntervalInBackground only covers the
  // app/window being unfocused, which is a different thing.
  const isFocused = useIsFocused();
  // Scroll gate for the poll below -- true while the list sits at the
  // newest end (offset 0 of an inverted list), which is where it opens.
  const [atNewest, setAtNewest] = useState(true);
  // The composer sits collapsed as a one-line bar until asked for (issue
  // #412), so the conversation gets the whole screen. It opens on a tap,
  // and by itself when the user scrolls back down to the newest end; it
  // closes after a send and when it is left empty. Draft text and photos
  // live in state above this, so collapsing never loses them.
  const [composerOpen, setComposerOpen] = useState(false);

  // Resolved hex values for the inline-SVG EmptyState icons below --
  // react-native-svg can't read Tamagui token strings ('$color'), the
  // same reason threads/thread-detail resolve actionPrimary this way.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  const [draft, setDraft] = useState('');
  // Sending from deep in the history should land the user back on the
  // newest message; in an inverted list that is offset 0. Also the ref
  // the FlatList below is attached to.
  const listRef = useRef<FlatList<Message>>(null);

  const messagesQuery = useInfiniteQuery({
    queryKey: queryKeys.messages(connectionId as string),
    queryFn: ({ pageParam }) => api.listMessages(connectionId as string, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.nextCursor ? lastPage.nextCursor : undefined),
    enabled: connectionId !== undefined,
    // A reply appears without a manual refresh -- but only while the user
    // is at the newest end of the conversation. TanStack refetches EVERY
    // loaded page on each interval tick and v5 has no refetchPage to
    // scope that to one, so an unconditional poll on a conversation
    // scrolled back ten pages issues ten sequential requests every 5s.
    // Gating on scroll position makes the common case one request, and
    // reading history pauses the poll entirely.
    //
    // The gone check stops a permanently-404 conversation being polled
    // forever: retry: false below bounds retry-on-error, but query-core's
    // own #shouldScheduleTimer checks only `enabled` and a valid timeout,
    // never error state. It is re-evaluated on every state update, so a
    // 404 mid-session clears the interval that is already running.
    //
    // ponytail: still O(loaded pages) once the user returns to the newest
    // end after scrolling deep. Upgrade path -- move the newest page into
    // its own plain useQuery for the interval to poll, and let this
    // infinite query serve only immutable scrolled-back history.
    refetchInterval: (query) => (isFocused && atNewest && !isGoneState(query.state) ? MESSAGE_POLL_MS : false),
    // retry: false, same as thread-detail's own 404-able query: a 404
    // here is a stable outcome, not a transient failure. TanStack's
    // default of three backoff attempts would spend ~7s re-asking a
    // route that is deliberately going to answer the same way every
    // time, and would delay both the "conversation is gone" state and a
    // failed poll's stale-data notice. A first-load failure that IS
    // transient still reaches the user as the error state with its own
    // "Try again".
    retry: false,
  });

  // The counterpart's identity comes from the complete connections
  // list -- useAllConnections reads the ONE ['me','connections'] cache
  // every other consumer shares (see hooks/use-connections.ts) and
  // waits for the full cursor chain, so a counterpart past the first
  // page is still found.
  const connections = useAllConnections();

  const connection = useMemo(
    () => connections.data?.find((c) => c.id === connectionId),
    [connections.data, connectionId],
  );

  const markRead = useMutation({
    mutationFn: () => api.markConversationRead(connectionId as string),
    onSuccess: () => {
      // Clears this conversation's badge on the Matches list -- both
      // the connections cache and the /me/activity poller's badge
      // counts (issue #395), so the tab badge drops now, not on the
      // next 30s poll.
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
    },
  });

  const attachments = useAttachmentPicker();

  const send = useMutation({
    mutationFn: (body: string) => api.sendMessage(connectionId as string, body, attachments.photoIds),
    onSuccess: () => {
      setDraft('');
      attachments.reset();
      setComposerOpen(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.messages(connectionId as string) });
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // The sent message lands at index 0 (the bottom) once the
      // invalidated query refetches, so this scrolls back to the newest
      // end even when the user sent it from deep in the history.
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    },
  });

  // Leaving the conversation for good. The label below states the real
  // consequence rather than the softer "unmatch": revoking creates a
  // Block against the other party (BlockSourceRevoke, see
  // api.revokeConnection's own comment) -- the same reason
  // reveal-requests words it this way.
  //
  // Invalidates ['me', 'connections'] (queryKeys.connections()), the
  // key BOTH this screen's counterpart lookup and the Matches list
  // read, so the revoked row is gone from Matches by the time
  // router.back() lands there rather than sitting there until the next
  // refetch.
  const unmatch = useMutation({
    mutationFn: () => api.revokeConnection(connectionId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // Revoke always creates a Block server-side, and the threads feed
      // excludes threads whose creator is blocked either way -- so without
      // these the blocked person's threads and posts stay on screen and
      // tapping one errors. Same three invalidations as the reveal-requests
      // screen's revoke, which is the same action (one action role, one
      // set of effects, everywhere).
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      queryClient.invalidateQueries({ queryKey: queryKeys.thread() });
      router.back();
    },
  });

  // Optimistic: the menu label flips at once, rolled back if the save
  // fails. Muting only silences pushes, so no other cache is touched
  // beyond the connections list that carries the flag and the activity
  // poll the badges read.
  const setMuted = useMutation({
    mutationFn: (muted: boolean) =>
      muted ? api.muteConnection(connectionId as string) : api.unmuteConnection(connectionId as string),
    onMutate: async (muted) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.connections() });
      const previous = queryClient.getQueryData<InfiniteData<ConnectionsPage>>(queryKeys.connections());

      queryClient.setQueryData<InfiniteData<ConnectionsPage>>(queryKeys.connections(), (data) =>
        setConnectionMuted(data, connectionId as string, muted),
      );

      return { previous };
    },
    onError: (_error, _muted, context) => {
      queryClient.setQueryData(queryKeys.connections(), context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
    },
  });

  // Every loaded page, newest-first -- the order the inverted FlatList
  // below renders, so index 0 sits at the bottom of the screen and the
  // conversation opens on its newest message instead of its oldest. No
  // reverse: the API already answers newest-first. The hook also carries
  // the list machine's guarded onEndReached fetch; this screen does NOT
  // route its full render through the shared state machine because its
  // 404 branch is the terminal 'gone' conversation state
  // (conversationStatus), not a retryable list error.
  const messages = usePaginatedResource(messagesQuery, (pages) => flattenMessagePages(pages ?? []));
  const hasOlder = hasOlderMessages(messagesQuery.data?.pages ?? []);

  // Mark read once the conversation loads, and again whenever a poll
  // brings a newer message in -- so the badge clears without the user
  // having to leave and come back. Keyed on the newest message id, not
  // the whole array, so a poll that changes nothing does not re-fire.
  const newestMessageId = messages.items[0]?.id;

  useEffect(() => {
    if (newestMessageId === undefined || currentUserId === undefined) return;

    markRead.mutate();
    // markRead is a stable mutation object; re-firing on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestMessageId, currentUserId]);

  // Not while a photo is still uploading: it would be sent without it.
  const canSend =
    hasSendableContent(draft, attachments.photoIds.length) &&
    draft.length <= MAX_MESSAGE_LEN &&
    !attachments.isUploading;

  const status = conversationStatus({
    isPending: messagesQuery.isPending,
    isError: messagesQuery.isError,
    hasData: messagesQuery.data !== undefined,
    errorStatus: messagesQuery.error instanceof ApiError ? messagesQuery.error.status : undefined,
  });

  return (
    <ScreenContainer>
      {/* Same Android reasoning as create-thread's composer -- see that
          file's comment (Expo's default adjustResize already handles it;
          RN's own 'height' would double-apply). */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <YStack flex={1}>
          {/* Rendered before the state branches below, not inside the
              success path -- this route sets headerShown: false (see
              (app)/_layout.tsx), so on web this link is the only way out
              of the screen, and it must exist while the conversation is
              still loading or has failed, not only once it has loaded.
              Same reasoning as thread-detail's own Back link. */}
          <YStack>
            <ScreenHeader onBack={() => router.back()} />
          </YStack>

          {status === 'loading' ? (
            <MessagesSkeleton />
          ) : status === 'gone' ? (
            // No retry action, deliberately -- see conversationStatus's
            // own comment. Every cause of this 404 is permanent, so a
            // "Try again" here could only ever fail again.
            <EmptyState
              icon={<MessageIcon size={24} strokeWidth={1.75} color={colors.actionPrimary} />}
              title="This conversation is gone"
              description="The connection may have been removed."
            />
          ) : status === 'failed' ? (
            <EmptyState
              icon={<AlertIcon size={24} strokeWidth={1.75} color={colors.actionPrimary} />}
              title="Couldn't load this conversation"
              description="Check your connection and try again."
              action={{ label: 'Try again', onPress: () => messagesQuery.refetch() }}
            />
          ) : (
            <>
              {connection && (
                <YStack
                  pt="$2"
                  pb="$3"
                  minHeight={96}
                  flexShrink={0}
                  zIndex={1}
                  backgroundColor="$background"
                  borderBottomWidth={borderWidth.hairline}
                  borderBottomColor="$borderColor"
                >
                  {/* One header row: the identity keeps its own tap
                      target (the profile, now one tap further on than it
                      used to be -- the Matches row opens this screen),
                      with the conversation's overflow menu in the
                      trailing slot. flex:1 on the identity, not
                      justifyContent="space-between", so the menu never
                      crowds a long handle off the edge. */}
                  <XStack alignItems="center" gap="$2">
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`View ${displayHandle(connection.otherUserHandle)}'s profile`}
                      {...hit(8)}
                      style={{ flex: 1 }}
                      onPress={() =>
                        router.push({ pathname: '/users/[userId]', params: { userId: connection.otherUserId } })
                      }
                    >
                      <YStack alignItems="center" gap="$1">
                        <PseudonymousAvatar handle={connection.otherUserHandle} id={connection.otherUserId} size={44} />
                        <Heading fontSize="$title" lineHeight="$title" numberOfLines={1}>
                          {displayHandle(connection.otherUserHandle)}
                        </Heading>
                      </YStack>
                    </Pressable>
                    <OverflowMenu
                      label="Conversation options"
                      actions={[
                        {
                          label: connection.muted ? 'Unmute conversation' : 'Mute conversation',
                          onPress: () => setMuted.mutate(!connection.muted),
                        },
                        {
                          // The label states the real consequence:
                          // revoking creates a Block (design.md's own
                          // data-model rule), and the reveal-requests
                          // screen already words it this way. A softer
                          // synonym here would be the same action
                          // wearing a friendlier face.
                          label: unmatch.isPending ? 'Unmatching...' : 'Unmatch and block',
                          onPress: () => {
                            if (!unmatch.isPending) unmatch.mutate();
                          },
                          destructive: true,
                        },
                      ]}
                    />
                  </XStack>
                </YStack>
              )}

              {/* Under the header, above the thread -- an unmatch that
                  failed leaves the conversation fully intact and says so,
                  rather than appearing to have worked. Same copy shape as
                  the send error below. */}
              {unmatch.isError && (
                <YStack pb="$2">
                  <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta" textAlign="center">
                    {unmatch.error.message || 'Could not unmatch. Check your connection and try again.'}
                  </Paragraph>
                </YStack>
              )}

              <FlatList
                ref={listRef}
                style={{ flex: 1 }}
                // Inverted (issue #64's chat-pagination fix): index 0 --
                // the newest message, see messages.items above -- renders at
                // the bottom, so the screen opens on the reply the Matches
                // row just previewed rather than on the conversation's
                // first message. It also means the END of this list is the
                // OLDER end, which is what onEndReached below loads, and
                // that the footer renders at the visual top.
                inverted
                data={messages.items}
                keyExtractor={(message) => message.id}
                // flexGrow 1 so the empty state below owns the list's
                // viewport (components.md's own composition rule) instead
                // of shrinking to its content -- in an inverted list a
                // short content box is anchored to the BOTTOM, which would
                // otherwise pin "Say hello" against the composer. No
                // effect once the conversation is taller than the screen.                // paddingTop, not paddingBottom: the list is inverted, so
                // VirtualizedList mirrors the content container and
                // paddingBottom would land at the VISUAL top. What this
                // padding is for is the gap between the newest bubble and
                // the composer below it, which is the visual bottom.
                contentContainerStyle={{ paddingTop: space[4], paddingBottom: space[3], gap: space[2], flexGrow: 1 }}
                // The poll gate above: offset 0 is the newest end, so
                // anything past 80px means the user is reading history and
                // the interval should stay off. 80 rather than 0 so a
                // one-line overscroll (iOS bounce) or a pixel of momentum
                // doesn't pause the poll.
                onScroll={(event) => {
                  const nowAtNewest = event.nativeEvent.contentOffset.y < 80;

                  // Back at the newest end after reading history: the user
                  // is about to reply, so bring the composer back.
                  if (nowAtNewest && !atNewest) setComposerOpen(true);
                  setAtNewest(nowAtNewest);
                }}
                scrollEventThrottle={16}
                // Guarded on hasNextPage so this doesn't refire once the
                // last page has been fetched, and on !isFetchingNextPage so
                // a fast scroll can't fire it twice concurrently -- the
                // guard now lives once in usePaginatedResource. In an
                // inverted list "reached the end" is the user scrolling UP
                // into older history, which is exactly where older pages
                // belong.
                onEndReached={messages.fetchNextPage}
                onEndReachedThreshold={0.5}
                ListFooterComponent={
                  // The shared footer with this screen's own labels --
                  // "earlier messages" instead of "more". Only mounted
                  // while older messages actually exist (hasOlderMessages
                  // is the cursor rule getNextPageParam is built on, read
                  // here so the cue disappears exactly when the history
                  // does).
                  hasOlder ? (
                    <ResourceListFooter
                      isFetchingNextPage={messages.isFetchingNextPage}
                      isFetchNextPageError={messages.isFetchNextPageError}
                      onRetry={messages.fetchNextPage}
                      errorLabel="Couldn't load earlier messages"
                      loadingLabel="Loading earlier messages"
                    />
                  ) : null
                }
                ListEmptyComponent={
                  // EmptyState's own full-viewport centering default, not
                  // the flex={0}/top-anchored override the other in-list
                  // call sites use: this one is inside an inverted list,
                  // where a short content box is anchored to the BOTTOM,
                  // so a top-anchored block would pin "Say hello" against
                  // the composer. gap="$2" is the one override the screen
                  // already had (a conversation with no messages yet has
                  // only these two lines).
                  <EmptyState
                    gap="$2"
                    title="Say hello"
                    description="This conversation is just between the two of you."
                  />
                }
                renderItem={({ item }) => <MessageBubble message={item} isOwn={item.senderId === currentUserId} />}
              />

              {/* A poll failed but the conversation is still on screen --
                  says so rather than silently going quiet. Never swaps the
                  thread out: the messages above are still real (see
                  conversationStatus's own comment). */}
              {messagesQuery.isError && <StaleRefreshNotice pb="$2" />}

              {composerOpen || send.isError ? (
                <YStack gap="$2" pt="$3" pb="$4" borderTopWidth={borderWidth.hairline} borderTopColor="$borderColor">
                  {/* Collapse is an explicit control, not a side effect of
                      blur: a blur timer raced the Attach tap and lost
                      drafts of intent. The draft and photos survive it. */}
                  <XStack justifyContent="flex-end" marginBottom={-8}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Collapse message box"
                      {...hit(8)}
                      onPress={() => {
                        // A failed send holds the composer open (send.isError);
                        // dismissing must clear that too.
                        send.reset();
                        setComposerOpen(false);
                      }}
                      style={({ pressed }) => ({
                        width: 44,
                        height: 32,
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: pressed ? 0.6 : 1,
                      })}
                    >
                      <ChevronDown size={24} strokeWidth={1.75} color={colors.textSecondary} />
                    </Pressable>
                  </XStack>

                  {send.isError && (
                    <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                      {send.error.message || 'Could not send. Check your connection and try again.'}
                    </Paragraph>
                  )}

                  <TextArea
                    autoFocus
                    value={draft}
                    onChangeText={setDraft}
                    placeholder="Write a message"
                    maxLength={MAX_MESSAGE_LEN}
                    accessibilityLabel="Message"
                  />

                  {/* Photos on the left, the send action on the right, one
                      row: the composer stays short instead of stacking
                      four full-width rows. */}
                  <XStack alignItems="flex-end" justifyContent="space-between" gap="$3">
                    <YStack flex={1} minWidth={0}>
                      <AttachmentPicker picker={attachments} locked={send.isPending} />
                    </YStack>

                    {/* Its accessible name is the visible "Send" label; while
                      sending, PrimaryButton's own `loading` prop carries the
                      busy announcement (issue #147 AC5) that swapping the
                      label for a spinner would otherwise drop. */}
                    <PrimaryButton
                      size="sm"
                      disabled={!canSend}
                      loading={send.isPending}
                      onPress={() => {
                        if (!send.isPending && canSend) send.mutate(draft.trim());
                      }}
                    >
                      {send.isPending ? <ActivityIndicator color="white" /> : 'Send'}
                    </PrimaryButton>
                  </XStack>
                </YStack>
              ) : (
                <YStack pt="$3" pb="$4" borderTopWidth={borderWidth.hairline} borderTopColor="$borderColor">
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Write a message"
                    onPress={() => setComposerOpen(true)}
                    style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                  >
                    <XStack
                      minHeight={44}
                      alignItems="center"
                      px="$4"
                      borderWidth={borderWidth.hairline}
                      borderColor="$borderColor"
                      borderRadius="$full"
                      backgroundColor="$card"
                    >
                      <Text color="$colorSecondary" numberOfLines={1} flex={1} textAlign="left">
                        {composerPillLabel(draft, attachments.photoIds.length)}
                      </Text>
                      {/* The collapsed pill read as a disabled field; a send
                          glyph says it is where a message starts. */}
                      <SendIcon size={20} strokeWidth={1.75} color={colors.actionPrimary} />
                    </XStack>
                  </Pressable>
                </YStack>
              )}
            </>
          )}
        </YStack>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}

// The older-messages cue (ListFooterComponent, the visual TOP of an
// inverted list) moved to the shared ResourceListFooter with this
// screen's own labels, mounted only while hasOlder. A failed
// older-page fetch still states inline with a retry and never clears
// the pages already on screen -- separate from the screen's own
// full-bleed `failed` state, which is a FIRST-load failure.

// One message row. Own messages align right with the brand fill; the
// counterpart's align left on the card surface -- the standard chat
// convention, and the only thing distinguishing the two parties in a
// screen that deliberately shows no other identity. Alignment carries
// the distinction as well as the fill does, so nothing here is
// conveyed by color alone.
function MessageBubble({ message, isOwn }: { message: Message; isOwn: boolean }) {
  // A photo sent on its own (issue #413) is just the photo: no bubble
  // fill, border or padding around it, and no empty text line under it.
  const hasText = message.body.trim() !== '';
  // The bubble is capped at 80% of the list width (16px gutters), so a fixed
  // 260px image overflows below about 340px screens.
  const { width: screenWidth } = useWindowDimensions();
  const imageWidth = Math.min(260, Math.floor((screenWidth - 32) * 0.8) - (hasText ? 24 : 0));

  return (
    <YStack alignItems={isOwn ? 'flex-end' : 'flex-start'} gap="$1">
      <YStack
        backgroundColor={hasText ? (isOwn ? '$primary' : '$card') : undefined}
        borderRadius="$md"
        borderWidth={hasText && !isOwn ? 1 : 0}
        borderColor="$borderColor"
        px={hasText ? '$3' : 0}
        py={hasText ? '$2' : 0}
        maxWidth="80%"
      >
        <YStack gap="$2">
          <AttachmentList attachments={message.attachments} width={imageWidth} />
          {hasText && (
            <Text color={isOwn ? '$colorOnAction' : '$color'}>
              <Linkified linkColor={isOwn ? '$colorOnAction' : '$colorLink'}>{message.body}</Linkified>
            </Text>
          )}
        </YStack>
      </YStack>
      <Text color="$colorTertiary" fontSize="$caption" lineHeight="$caption">
        {relativeTime(message.createdAt)}
      </Text>
    </YStack>
  );
}

// Issue #147 AC3: the first-load state used to be a bare, centered
// ActivityIndicator, with nothing else on screen -- which reads as "this
// page failed to load," not "messages are coming." Three bubble-shaped
// placeholders, alternating sides the way the real conversation will,
// so the swap-in when the messages arrive doesn't jump.
function MessagesSkeleton() {
  return (
    <YStack flex={1} pt="$6" gap="$3">
      <Skeleton height={44} width="62%" />
      <Skeleton height={44} width="48%" alignSelf="flex-end" />
      <Skeleton height={44} width="56%" />
    </YStack>
  );
}
